import React from 'react';
import {observer} from 'mobx-react';

import {
    CERTIFICATION_BADGE,
    DIRECTORY_SEARCH_RESULT_CARD_LINK,
    PRACTICE_LOC_CONTACT_TYPE,
    PROFILE_CARD,
    SEE_OUT_OF_POCKET_COSTS,
} from 'core/constants.js';
import {PracticeStoreContextV2} from 'core/stores/RootStore.js';
import {partitionArray} from 'utils/arrays.js';
import {hexToRgba} from 'utils/colors.js';

import {CardV2, NotificationV2} from '@healthshareau/hs-component-library';

@observer
class PractitionerListV2 extends React.Component {
    static contextType = PracticeStoreContextV2;

    participationBannerText(healthFund, hasParticipation = true) {
        return (
            <>
                The following practitioners may{' '}
                {!hasParticipation ? 'not ' : ''}partner with{' '}
                <strong>{healthFund.name}</strong> at this location
            </>
        );
    }

    renderNonParticipationBanner(healthFund) {
        return (
            <NotificationV2
                dataTestId="health-fund-non-participation-banner"
                type="error"
                text={this.participationBannerText(healthFund, false)}
            />
        );
    }

    renderParticipationBanner(healthFund) {
        const notifImg = (
            <img src={healthFund.avatar} alt={`${healthFund.name} logo`} />
        );
        return (
            <NotificationV2
                customClass="phi-banner-override"
                colorTheme={{
                    color: !!healthFund.colorBrand
                        ? `#${healthFund.colorBrand}`
                        : '',
                    backgroundColor: !!healthFund.colorBrand
                        ? hexToRgba(healthFund.colorBrand, 0.1)
                        : '',
                    padding: '16px',
                }}
                dataTestId="health-fund-participation-banner"
                displayImage={notifImg}
                text={this.participationBannerText(healthFund)}
            />
        );
    }

    renderCard(practitioner, idx, additionalProps) {
        const {profileCardCTA, practice} = this.context;
        const {
            avatar,
            certifications,
            displayName,
            offersTelehealth,
            interestsDescription,
            profileUrl,
            specialties,
        } = practitioner;
        const specialtiesStr = specialties
            .map((specialty) => specialty.name)
            .join(', ');
        const profileCardProps = {
            ...additionalProps,
            alt: displayName,
            asElement: 'li',
            avatar: {
                src: !avatar || avatar.includes('empty_avatar') ? '' : avatar,
            },
            button: profileCardCTA(
                practitioner,
                PROFILE_CARD,
                PRACTICE_LOC_CONTACT_TYPE,
            ),
            certificateChips: certifications,
            certificateTracking: {
                gaObjectType: PROFILE_CARD,
                gaSelector: CERTIFICATION_BADGE,
                dataTestId: CERTIFICATION_BADGE,
            },
            dataTestId: PROFILE_CARD,
            gaObjectType: PROFILE_CARD,
            gaSelector: DIRECTORY_SEARCH_RESULT_CARD_LINK,
            header: displayName,
            href: profileUrl,
            key: idx,
            offersHicaps: practice.offersHicaps,
            offersTelehealth: offersTelehealth,
            specialInterests: interestsDescription,
            subheader: specialtiesStr,
        };
        return <CardV2 {...profileCardProps} />;
    }
    render() {
        const {
            hasPractitioners,
            gapSchemePractitioners,
            nonGapSchemePractitioners,
            shouldDisplayParticipatingGroupedV2,
            practice,
            isFetchingPracticeInfo,
            rootStore: {
                healthFundStore: {healthFund},
                paramStore: {hasSkin},
            },
        } = this.context;
        if (!hasPractitioners) {
            return null;
        }

        if (healthFund?.id && !isFetchingPracticeInfo) {
            const participatingStyle = {
                color: `#${healthFund.colorBrand}`,
            };
            const colorTheme = hasSkin ? participatingStyle : null;

            // for backwards compatibility with v1 logic
            // We will check if we are using the v2 api
            // if using v1 we will revert back to just checking gap scheme membership as per normal
            // Hopefully we can come back and clean this up later
            let [participating, nonParticipating] = [
                gapSchemePractitioners,
                nonGapSchemePractitioners,
            ];

            // v2 checks hfr as well as gap scheme
            if (shouldDisplayParticipatingGroupedV2) {
                [participating, nonParticipating] = partitionArray(
                    practice.practitioners,
                    (p) => p.isParticipatingWithHealthFund,
                );
            }

            return (
                <>
                    {!!participating?.length && (
                        <>
                            {this.renderParticipationBanner(healthFund)}
                            <ul>
                                {participating.map((practitioner, idx) =>
                                    this.renderCard(practitioner, idx, {
                                        colorTheme,
                                        feesColor: participatingStyle,
                                        feesChip: {
                                            feesUrl: `${practitioner.profileUrl}?health_fund=${healthFund.id}#fees`,
                                            gaObjectType: PROFILE_CARD,
                                            gaSelector: SEE_OUT_OF_POCKET_COSTS,
                                        },
                                    }),
                                )}
                            </ul>
                        </>
                    )}
                    {!!nonParticipating?.length && (
                        <>
                            {this.renderNonParticipationBanner(healthFund)}
                            <ul>
                                {nonParticipating.map((practitioner, idx) =>
                                    this.renderCard(practitioner, idx, {
                                        colorTheme,
                                    }),
                                )}
                            </ul>
                        </>
                    )}
                </>
            );
        } else {
            return (
                <ul>
                    {practice.practitioners?.map((practitioner, idx) =>
                        this.renderCard(practitioner, idx),
                    )}
                </ul>
            );
        }
    }
}

export default PractitionerListV2;
